import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { adminTotpStatus } from "@/lib/api";
import { Loader2, ShieldAlert } from "lucide-react";

/**
 * Admin guard — requires:
 * 1. authenticated user
 * 2. user.is_admin === true
 * 3. TOTP set up (else → /admin/setup)
 * 4. current session admin-2FA verified (else → /admin/verify)
 *
 * We refetch status on every mount so a stale cache never grants access.
 */
export default function AdminGuard() {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    if (!user || authLoading) return;
    if (!user.is_admin) {
      setLoading(false);
      return;
    }
    // Invalidate stale status when path changes so redirects can't fire on old data.
    setStatus(null);
    setLoading(true);
    (async () => {
      try {
        const s = await adminTotpStatus();
        if (mounted) setStatus(s);
      } catch {
        if (mounted) setStatus({ is_admin: false });
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [user, authLoading, location.pathname]);

  if (authLoading || loading || (user?.is_admin && !status)) {
    return (
      <div className="min-h-screen grid place-items-center bg-background">
        <Loader2 className="w-6 h-6 animate-spin text-foreground/60" data-testid="admin-guard-loading" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  if (!user.is_admin || !status?.is_admin) {
    return (
      <div className="min-h-screen grid place-items-center bg-background px-6" data-testid="admin-guard-forbidden">
        <div className="max-w-md text-center">
          <div className="w-14 h-14 rounded-2xl bg-foreground/[0.04] border border-black/[0.08] grid place-items-center mx-auto mb-4">
            <ShieldAlert className="w-7 h-7 text-foreground/60" />
          </div>
          <h1 className="font-display text-3xl font-black mb-2">Zona restringida</h1>
          <p className="text-sm text-foreground/60">Necesitas ser administrador para acceder aquí.</p>
        </div>
      </div>
    );
  }

  if (!status.totp_enabled && location.pathname !== "/admin/setup") {
    return <Navigate to="/admin/setup" replace />;
  }

  // Already set up but user landed on /admin/setup again: bounce them out.
  if (status.totp_enabled && location.pathname === "/admin/setup") {
    return <Navigate to="/admin" replace />;
  }

  // Already verified but user landed on /admin/verify: bounce them into the panel.
  if (status.totp_enabled && status.verified && location.pathname === "/admin/verify") {
    return <Navigate to="/admin" replace />;
  }

  if (status.totp_enabled && !status.verified && location.pathname !== "/admin/verify") {
    return <Navigate to="/admin/verify" replace state={{ from: location.pathname }} />;
  }

  return <Outlet context={{ status }} />;
}
